import React, { useEffect, useState } from "react";
import { Badge, Breadcrumb, BreadcrumbItem, Button, Card, Col, Container, Form, Modal, Nav, Row, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEye, FaCheck, FaTimes } from "react-icons/fa";
import { attendanceService } from "../../services/attendanceService";

const AdminAttendanceReviewScreen = () => {
  const [dateStr, setDateStr] = useState(new Date().toISOString().split('T')[0]);
  const [staffList, setStaffList] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("pending");
  const [reviewRemark, setReviewRemark] = useState("");

  const { userInfo } = useSelector((state) => state.auth);
  const ReqCompanyCode = (userInfo && userInfo[13]?.InfoField) || "";

  const fetchData = async () => {
    try {
      const formattedDate = new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).replace(/\//g, '-');

      const response = await attendanceService.getAdminDailyAttendance(formattedDate, "", ReqCompanyCode);
      setStaffList(Array.isArray(response) ? response : []);

    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Failed to fetch attendance data");
      setStaffList([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateStr]);

  const handleReview = async (status, remarks) => {
    if (!remarks.trim()) {
      toast.error("Please enter remarks");
      return;
    }
    if (!selectedMember?.AttID) {
      toast.error("No attendance entry selected for review.");
      return;
    }

    try {
      const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1);

      const response = await attendanceService.updateAttendanceReview(
        selectedMember.AttID,
        capitalizedStatus,
        remarks,
        ReqCompanyCode
      );
      if (response && response[0]?.ActionType > 0) {
        toast.success("Review status updated successfully");
        setShowModal(false);
        fetchData(); // Refresh the list
      } else {
        toast.error(response[0]?.ErrorMessage || "Failed to update review status");
      }
    } catch (error) {
      console.error("Error updating review:", error);
      toast.error(error.message || "Failed to update review status");
    }
  };

  const filteredStaffList = staffList.filter((staff) => {
    return staff.reviewsts?.toLowerCase() === reviewStatus.toLowerCase();
  });

  return (
    <>
      <Breadcrumb>
        <BreadcrumbItem>
          <Nav.Link as={Link} to="/dashboard" className="text-success text-decoration-none">
            Dashboard
          </Nav.Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Nav.Link as={Link} to="/admin-attendance-management" className="text-success text-decoration-none">
            Attendance Management
          </Nav.Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Attendance Review</BreadcrumbItem>
      </Breadcrumb>

      <Container className="py-4">
        <Row className="mb-4">
          <Col>
            <h4>Attendance Review</h4>
          </Col>
          <Col xs="auto">
            <Form.Control
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
            />
          </Col>
        </Row>

        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link
              active={reviewStatus === "pending"}
              onClick={() => setReviewStatus("pending")}
            >
              Pending
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={reviewStatus === "approved"}
              onClick={() => setReviewStatus("approved")}
            >
              Approved
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={reviewStatus === "rejected"}
              onClick={() => setReviewStatus("rejected")}
            >
              Rejected
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>View</th>
                <th>Name</th>
                <th>Type</th>
                <th>Entry Date</th>
                <th>Entry Time</th>
                <th>Designation</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredStaffList.map((staff) => (
                <tr key={staff.AttID}>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setSelectedMember(staff);
                        setShowModal(true);
                      }}
                    >
                      <FaEye />
                    </Button>
                  </td>
                  <td>{staff.EmployeeName}</td>
                  <td>{staff.AttType}</td>
                  <td>{new Date(staff.AttDateTime).toLocaleDateString()}</td>
                  <td>{new Date(staff.AttDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</td>
                  <td>{staff.Designation}</td>
                  <td>
                    <Badge bg={
                      staff.reviewsts?.toLowerCase() === "approved" ? "success" :
                      staff.reviewsts?.toLowerCase() === "rejected" ? "danger" :
                      "warning"
                    }>
                      {staff.reviewsts || "Pending"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Attendance Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedMember && (
              <div>
                <Row>
                  <Col md={6}>
                    <h5>Employee Information</h5>
                    <p><strong>Name:</strong> {selectedMember.EmployeeName}</p>
                    <p><strong>ID:</strong> {selectedMember.EMPCODE}</p>
                    <p><strong>Designation:</strong> {selectedMember.Designation}</p>
                    <p><strong>Type:</strong> {selectedMember.AttType}</p>
                    <p><strong>Entry Date:</strong> {new Date(selectedMember.AttDateTime).toLocaleDateString()}</p>
                    <p><strong>Entry Time:</strong> {new Date(selectedMember.AttDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</p>
                  </Col>
                  <Col md={6}>
                    {selectedMember.AttImage && (
                      <img
                        src={`${process.env.REACT_APP_API_URL}${selectedMember.AttImage}`}
                        alt="Attendance"
                        className="img-fluid rounded"
                      />
                    )}
                  </Col>
                </Row>
                <hr />
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Review Status</Form.Label>
                    <Form.Select
                      value={reviewStatus}
                      onChange={(e) => setReviewStatus(e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={reviewRemark}
                      onChange={(e) => setReviewRemark(e.target.value)}
                      placeholder="Enter your remarks here..."
                    />
                  </Form.Group>
                </Form>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button
              variant={reviewStatus === "approved" ? "success" : "danger"}
              onClick={() => handleReview(reviewStatus, reviewRemark)}
            >
              {reviewStatus === "approved" ? <FaCheck /> : <FaTimes />} Update Review
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default AdminAttendanceReviewScreen; 